import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  message?: string | null;
  children: React.ReactNode;
  className?: string;
}

/** Minimum gap kept between the tooltip and the viewport edges. */
const VIEWPORT_PADDING = 8;

export const Tooltip: React.FC<TooltipProps> = ({ message, children, className = '' }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; centerX: number }>({ top: 0, centerX: 0 });
  const [left, setLeft] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Centre over the trigger, then nudge back inside the viewport so the bubble never gets
  // squeezed or clipped near a screen edge; the arrow stays pointed at the trigger.
  useLayoutEffect(() => {
    if (!isHovered || !tooltipRef.current) return;
    const width = tooltipRef.current.offsetWidth;
    const maxLeft = window.innerWidth - width - VIEWPORT_PADDING;
    setLeft(Math.max(VIEWPORT_PADDING, Math.min(anchor.centerX - width / 2, maxLeft)));
  }, [isHovered, anchor, message]);

  if (!message) {
    return <>{children}</>;
  }

  const handleMouseEnter = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setAnchor({
        top: rect.top - 8,
        centerX: rect.left + rect.width / 2,
      });
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const tooltipPortal = isHovered && (
    <div
      ref={tooltipRef}
      className="fixed z-[999999] w-max max-w-[260px] pointer-events-none -translate-y-full"
      style={{
        top: `${anchor.top}px`,
        left: `${left}px`,
      }}
    >
      <div className="bg-[#1E2230] border border-[#2E3446] text-white text-xs px-3 py-2 rounded-lg shadow-2xl text-left leading-5 whitespace-normal break-words">
        {message}
      </div>
      <div
        className="absolute top-full w-0 h-0 -translate-x-1/2 -mt-[1px] border-x-[5px] border-x-transparent border-t-[5px] border-t-[#2E3446]"
        style={{ left: `${anchor.centerX - left}px` }}
      />
    </div>
  );

  return (
    <div
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`inline-block ${className}`}
    >
      {children}
      {typeof document !== 'undefined' && createPortal(tooltipPortal, document.body)}
    </div>
  );
};

export default Tooltip;
